import React, { useState, useMemo, useEffect } from 'react';
// --- CAMBIO: Se añaden las funciones para llamar al backend ---
import { addDoc, collection, doc, deleteDoc, updateDoc, writeBatch, Timestamp, onSnapshot, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PackageList from '../ui/PackageList';
import PackageForm from '../ui/PackageForm';
import Modal from '../ui/Modal';
import { appId } from '../../App';

// --- CONSTANTES (Sin cambios) ---
const STATUS_OPTIONS = ["Enviado", "Entregado", "Anulado", "Devolución"];

// --- COMPONENTE PARA CABECERAS ORDENABLES (Sin cambios) ---
const SortableHeader = ({ children, columnKey, sortCriteria, setSortCriteria }) => {
    const isSorted = sortCriteria.startsWith(columnKey);
    const isAsc = isSorted && sortCriteria.endsWith('Asc');
    const isDesc = isSorted && !isAsc;

    const handleClick = () => {
        if (isSorted) {
            setSortCriteria(isAsc ? `${columnKey}Desc` : `${columnKey}Asc`);
        } else {
            setSortCriteria(columnKey.includes('Date') ? `${columnKey}Desc` : `${columnKey}Asc`);
        }
    };

    return (
        <th className="group text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer" onClick={handleClick}>
            <div className="flex items-center">
                <span>{children}</span>
                <div className="ml-1.5 flex flex-col">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 -mb-1 transition-colors ${isAsc ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 -mt-1 transition-colors ${isDesc ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>
        </th>
    );
};

// --- FUNCIONES AUXILIARES (Sin cambios) ---
const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Timestamp ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return 'Fecha Inválida';
    return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateForCSV = (date) => {
    if (!date) return '';
    const d = date instanceof Timestamp ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const exportToCSV = (dataToExport, showModalFn) => {
    if (dataToExport.length === 0) {
        showModalFn("Exportar CSV", "No hay datos para exportar con los filtros actuales.");
        return;
    }
    const headersForCSV = ["# Pedido", "Fecha Envío", "Mensajero", "Valor Producto", "Costo Envío", "Gastos", "Total Paquete", "Total Neto", "Estatus", "Forma de Pago", "Fecha Entrega", "Notas", "Autorización"];
    const csvRows = [headersForCSV.join(";")];

    dataToExport.forEach(item => {
        const rowData = {
            "# Pedido": item.orderId,
            "Fecha Envío": formatDateForCSV(item.shipDate),
            "Mensajero": item.courierName,
            "Valor Producto": (item.productValue || 0).toFixed(2),
            "Costo Envío": (item.shippingCost || 0).toFixed(2),
            "Gastos": (item.expenses || 0).toFixed(2),
            "Total Paquete": ((parseFloat(item.productValue) || 0) + (parseFloat(item.shippingCost) || 0)).toFixed(2),
            "Total Neto": (((parseFloat(item.productValue) || 0) + (parseFloat(item.shippingCost) || 0)) - (parseFloat(item.expenses) || 0)).toFixed(2),
            "Estatus": item.status,
            "Forma de Pago": item.paymentMethod,
            "Fecha Entrega": formatDateForCSV(item.deliveryDate),
            "Notas": item.notes,
            "Autorización": item.authorizationCode,
        };
        const row = headersForCSV.map(header => `"${String(rowData[header] ?? '').replace(/"/g, '""')}"`);
        csvRows.push(row.join(";"));
    });

    const csvString = csvRows.join("\r\n");
    const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `reporte_general_${new Date().toISOString().slice(0,10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const parseDateFromCSV = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (!parts) return null;
    const day = parseInt(parts[1], 10);
    const month = parseInt(parts[2], 10) - 1;
    const year = parseInt(parts[3], 10);
    const date = new Date(year, month, day);
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
        return null;
    }
    return date;
};

function getSafeDate(dateInput) {
    if (!dateInput) return null;
    if (dateInput instanceof Timestamp) return dateInput.toDate();
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return null;
    return date;
}

function RegistroGeneral({ showModal, closeModal, db, permissions }) {
    
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormVisibleForAdd, setIsFormVisibleForAdd] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentPackage, setCurrentPackage] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    
    const [searchTermGeneral, setSearchTermGeneral] = useState('');
    const [selectedStatusGeneral, setSelectedStatusGeneral] = useState('');
    const [sortCriteria, setSortCriteria] = useState('shipDateDesc');

    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');

    const [availableYears, setAvailableYears] = useState([]);
    const [availableMonths, setAvailableMonths] = useState({});

    useEffect(() => {
        const fetchAvailableDates = async () => {
            const packagesCollectionPath = `artifacts/${appId}/packages`;
            const q = query(collection(db, packagesCollectionPath));
            const querySnapshot = await getDocs(q);

            const dates = new Set();
            querySnapshot.forEach(doc => {
                const shipDate = doc.data().shipDate;
                if (shipDate && shipDate.toDate) {
                    const dateObj = shipDate.toDate();
                    const year = dateObj.getFullYear();
                    const month = dateObj.getMonth() + 1;
                    dates.add(`${year}-${month}`);
                }
            });

            const yearMonthMap = {};
            dates.forEach(ym => {
                const [year, month] = ym.split('-').map(Number);
                if (!yearMonthMap[year]) {
                    yearMonthMap[year] = new Set();
                }
                yearMonthMap[year].add(month);
            });

            const finalYears = Object.keys(yearMonthMap).sort((a, b) => b - a);
            const finalMonths = {};
            for (const year in yearMonthMap) {
                finalMonths[year] = Array.from(yearMonthMap[year]).sort((a, b) => a - b);
            }

            setAvailableYears(finalYears);
            setAvailableMonths(finalMonths);
        };

        fetchAvailableDates();
    }, [db]);

    useEffect(() => {
        if (!selectedYear || !selectedMonth) {
            setPackages([]);
            return;
        }

        setIsLoading(true);

        const startDate = new Date(selectedYear, selectedMonth - 1, 1);
        const endDate = new Date(selectedYear, selectedMonth, 1);

        const packagesCollectionPath = `artifacts/${appId}/packages`;
        const q = query(
            collection(db, packagesCollectionPath),
            where('shipDate', '>=', startDate),
            where('shipDate', '<', endDate)
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const packagesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                shipDate: doc.data().shipDate?.toDate ? doc.data().shipDate.toDate() : null,
                deliveryDate: doc.data().deliveryDate?.toDate ? doc.data().deliveryDate.toDate() : null,
            }));
            setPackages(packagesData);
            setIsLoading(false);
        }, (error) => {
            console.error("Error al cargar paquetes: ", error);
            showModal("Error de Carga", `No se pudieron cargar los paquetes del mes seleccionado: ${error.message}`);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [selectedYear, selectedMonth, db, showModal]);


    const handleAddPackage = async (packageData) => {
        if (!db) { showModal("Error", "BD no disponible."); return; }
        setIsLoading(true);
        try {
            const packagesCollectionPath = `artifacts/${appId}/packages`;
            const dataToSave = {
                ...packageData,
                productValue: parseFloat(packageData.productValue) || 0,
                shippingCost: parseFloat(packageData.shippingCost) || 0,
                expenses: parseFloat(packageData.expenses) || 0,
                shipDate: packageData.shipDate ? Timestamp.fromDate(getSafeDate(packageData.shipDate)) : Timestamp.now(),
                deliveryDate: packageData.deliveryDate ? Timestamp.fromDate(getSafeDate(packageData.deliveryDate)) : null,
                createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
            };
            await addDoc(collection(db, packagesCollectionPath), dataToSave);
            showModal("Éxito", "Paquete agregado correctamente.");
            setIsFormVisibleForAdd(false);
        } catch (error) {
            console.error("Error al agregar paquete: ", error);
            showModal("Error", `No se pudo agregar: ${error.message}`);
        }
        setIsLoading(false);
    };

    const handleUpdatePackage = async (packageId, packageData) => {
        if (!db) { showModal("Error", "BD no disponible."); return; }
        setIsLoading(true);
        try {
            const packageDocRef = doc(db, `artifacts/${appId}/packages`, packageId);
            let finalDataToUpdate = {...packageData};
            const originalPackageFromState = packages.find(p => p.id === packageId) || currentPackage;

            finalDataToUpdate.productValue = parseFloat(packageData.productValue) || 0;
            finalDataToUpdate.shippingCost = parseFloat(packageData.shippingCost) || 0;
            finalDataToUpdate.shipDate = packageData.shipDate ? Timestamp.fromDate(getSafeDate(packageData.shipDate)) : null;

            if (packageData.expenses !== undefined) {
                finalDataToUpdate.expenses = parseFloat(packageData.expenses) || 0;
            } else if (originalPackageFromState && originalPackageFromState.expenses !== undefined) {
                finalDataToUpdate.expenses = parseFloat(originalPackageFromState.expenses) || 0;
            } else {
                finalDataToUpdate.expenses = 0;
            }
            finalDataToUpdate.updatedAt = Timestamp.now();
            finalDataToUpdate.deliveryDate = packageData.deliveryDate ? Timestamp.fromDate(getSafeDate(packageData.deliveryDate)) : null;
            
            if (originalPackageFromState && originalPackageFromState.status && finalDataToUpdate.status &&
                originalPackageFromState.status.toLowerCase() === "enviado" &&
                (finalDataToUpdate.status.toLowerCase() === "entregado" || finalDataToUpdate.status.toLowerCase() === "anulado" || finalDataToUpdate.status.toLowerCase() === "devolución") &&
                !finalDataToUpdate.deliveryDate
            ) {
                finalDataToUpdate.deliveryDate = Timestamp.now();
            }

            await updateDoc(packageDocRef, finalDataToUpdate);
            showModal("Éxito", "Paquete actualizado.");
            setIsEditModalOpen(false);
            setCurrentPackage(null);
        } catch (error) {
            console.error("Error al actualizar: ", error);
            showModal("Error", `No se pudo actualizar: ${error.message}`);
        }
        setIsLoading(false);
    };

    const handleDeletePackage = (packageId) => {
        const confirmDelete = async () => {
            closeModal();
            if (!db) { showModal("Error", "BD no disponible."); return; }
            setIsLoading(true);
            try {
                await deleteDoc(doc(db, `artifacts/${appId}/packages`, packageId));
                showModal("Éxito", "Paquete eliminado.");
            } catch (error) { console.error("Error al eliminar:", error); showModal("Error", `No se pudo eliminar: ${error.message}`); }
            setIsLoading(false);
        };

        showModal("Confirmar Eliminación", "¿Eliminar este paquete? Esta acción no se puede deshacer.",
            <>
                <button onClick={confirmDelete} className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded mr-2">Eliminar</button>
                <button onClick={closeModal} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded">Cancelar</button>
            </>
        );
    };

    // --- CAMBIO: NUEVA FUNCIÓN PARA DISPARAR EL RECÁLCULO DE KPIs ---
    const triggerKpiRecalculation = async (year, month) => {
        // Le damos un pequeño respiro a la UI antes de mostrar el siguiente modal.
        await new Promise(resolve => setTimeout(resolve, 1500));

        const monthName = new Date(year, month - 1).toLocaleString('es-GT', { month: 'long' });
        showModal(
            "Actualizando KPIs", 
            `Se ha iniciado el recálculo de KPIs para ${monthName} de ${year}. Los datos se actualizarán en segundo plano en el módulo de KPIs.`
        );

        const functions = getFunctions();
        const forceRecalculation = httpsCallable(functions, 'forceRecalculation');

        try {
            await forceRecalculation({ year, month });
            console.log(`KPI recalculation triggered for ${year}-${month}`);
        } catch (error) {
            console.error("Error al disparar el recálculo de KPIs:", error);
            // No mostramos un modal de error aquí para no ser intrusivos.
            // El error principal (la carga de CSV) ya fue exitoso para el usuario.
        }
    };
    
    const handleFileUpload = async (file) => {
        if (!file) return;
        setIsUploading(true);
    
        const reader = new FileReader();
        reader.onload = async (event) => {
            let text = event.target.result;
            if (text.startsWith('\uFEFF')) {
                text = text.substring(1);
            }
            
            const lines = text.split(/\r\n|\n/).filter(line => line);
    
            if (lines.length < 2) {
                showModal("Error de Archivo", "El archivo CSV está vacío o no tiene datos.");
                setIsUploading(false);
                return;
            }
    
            const headerLine = lines[0].split(';').map(h => h.trim().replace(/"/g, ''));
            
            const headerMap = {
                'FECHA': 'shipDate', '# PEDIDO': 'orderId', 'VENTA': 'productValue', 'ENVIO': 'shippingCost', 'MOTORISTA': 'courierName',
                'SITUACION': 'status', 'GASTOS': 'expenses', 'FORMA DE PAGO': 'paymentMethod', 'FECHA ENTREGA': 'deliveryDate',
                'NOTAS': 'notes', 'AUTORIZACION': 'authorizationCode'
            };

            const requiredHeaders = ['FECHA', '# PEDIDO', 'VENTA', 'ENVIO', 'MOTORISTA'];
    
            const missingHeaders = requiredHeaders.filter(h => !headerLine.includes(h));
            if (missingHeaders.length > 0) {
                showModal("Error de Formato", `El archivo CSV no contiene las siguientes columnas requeridas: ${missingHeaders.join(', ')}`);
                setIsUploading(false);
                return;
            }
    
            const packagesCollectionPath = `artifacts/${appId}/packages`;
            const batch = writeBatch(db);
            const packagesToCreate = [];
    
            for (let i = 1; i < lines.length; i++) {
                const data = lines[i].split(';').map(field => field.trim().replace(/"/g, ''));
                const pkgData = {};
                
                headerLine.forEach((header, index) => {
                    const dbField = headerMap[header];
                    if (dbField) {
                        pkgData[dbField] = data[index] || '';
                    }
                });

                const shipDate = parseDateFromCSV(pkgData.shipDate);
                if (!shipDate) {
                    showModal("Error en Fila " + (i + 1), `La fecha en la columna FECHA '${pkgData.shipDate}' no tiene un formato válido (dd/mm/yyyy o dd-mm-yyyy).`);
                    setIsUploading(false);
                    return;
                }
    
                const packageForDB = {
                    orderId: pkgData.orderId, shipDate: Timestamp.fromDate(shipDate), courierName: pkgData.courierName,
                    productValue: parseFloat(String(pkgData.productValue).replace('Q','')) || 0,
                    shippingCost: parseFloat(String(pkgData.shippingCost).replace('Q','')) || 0,
                    expenses: parseFloat(pkgData.expenses) || 0, status: pkgData.status || "Enviado",
                    paymentMethod: pkgData.paymentMethod || "Efectivo",
                    deliveryDate: pkgData.deliveryDate && parseDateFromCSV(pkgData.deliveryDate) ? Timestamp.fromDate(parseDateFromCSV(pkgData.deliveryDate)) : null,
                    notes: pkgData.notes || "", authorizationCode: pkgData.authorizationCode || "",
                    createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
                };
                packagesToCreate.push(packageForDB);
            }
    
            try {
                packagesToCreate.forEach(pkgData => {
                    const newDocRef = doc(collection(db, packagesCollectionPath));
                    batch.set(newDocRef, pkgData);
                });
                await batch.commit();
                showModal("Éxito", `${packagesToCreate.length} paquetes han sido cargados correctamente.`);
                
                // --- CAMBIO: INICIAR RECÁLCULO AUTOMÁTICO DE KPIs ---
                if (packagesToCreate.length > 0) {
                    // Se determina el mes y año del último paquete cargado para recalcular ese período.
                    const lastPackage = packagesToCreate[packagesToCreate.length - 1];
                    const lastDate = lastPackage.shipDate.toDate();
                    const yearToRecalculate = lastDate.getFullYear();
                    const monthToRecalculate = lastDate.getMonth() + 1;
                    triggerKpiRecalculation(yearToRecalculate, monthToRecalculate);
                }

            } catch (error) {
                console.error("Error al cargar paquetes desde CSV:", error);
                showModal("Error de Carga", `Ocurrió un error al guardar los paquetes: ${error.message}`);
            } finally {
                setIsUploading(false);
                if (document.getElementById('csvFileInput')) {
                    document.getElementById('csvFileInput').value = ''; 
                }
            }
        };
        reader.onerror = () => {
             showModal("Error de Archivo", "No se pudo leer el archivo seleccionado.");
             setIsUploading(false);
        };
        reader.readAsText(file, 'UTF-8');
    };
    
    const openFormForEdit = (pkg) => {
        if (!pkg || !pkg.id) { showModal("Error de Edición", "Datos de paquete inválidos."); return; }
        const formatDateForInput = (date) => date ? new Date(date).toISOString().split('T')[0] : '';
        setCurrentPackage({ ...pkg, shipDate: formatDateForInput(pkg.shipDate), deliveryDate: formatDateForInput(pkg.deliveryDate), });
        setIsEditModalOpen(true);
    };

    const openFormForNew = () => {
        setCurrentPackage(null);
        setIsFormVisibleForAdd(true);
        setIsEditModalOpen(false);
    };
    
    const filteredAndSortedPackagesGeneral = useMemo(() => {
        const lowerCaseTerm = searchTermGeneral.toLowerCase().trim();

        const filtered = packages.filter(pkg => {
            const statusMatch = selectedStatusGeneral ? (pkg.status && pkg.status.toLowerCase() === selectedStatusGeneral.toLowerCase()) : true;
            if (!statusMatch) return false;
            
            if (!lowerCaseTerm) return true;
            
            const shipDateStr = pkg.shipDate ? formatDate(pkg.shipDate) : '';
            const deliveryDateStr = pkg.deliveryDate ? formatDate(pkg.deliveryDate) : '';

            return [ pkg.orderId, pkg.courierName, pkg.status, pkg.paymentMethod, shipDateStr, deliveryDateStr ]
                .some(field => String(field).toLowerCase().includes(lowerCaseTerm));
        });

        return [...filtered].sort((a, b) => { 
            const field = sortCriteria.replace(/Desc|Asc/, '');
            const direction = sortCriteria.endsWith('Desc') ? -1 : 1;
            const valA = a[field];
            const valB = b[field];

            if (valA === null || valA === undefined) return 1 * direction;
            if (valB === null || valB === undefined) return -1 * direction;

            if (valA instanceof Date && valB instanceof Date) {
                return (valA.getTime() - valB.getTime()) * direction;
            }

            if (!isNaN(parseFloat(valA)) && !isNaN(parseFloat(valB))) {
                return (Number(valA) - Number(valB)) * direction;
            }
            
            return String(valA).localeCompare(String(valB)) * direction;
        });
    }, [packages, searchTermGeneral, selectedStatusGeneral, sortCriteria]);
    
    const handleExportGeneralCSV = () => {
        exportToCSV(filteredAndSortedPackagesGeneral, showModal);
    };

    if (!permissions?.REGISTRO_GENERAL?.view) {
        return <div className="text-center py-10 bg-red-50 border border-red-200 rounded-lg shadow"><h2 className="text-2xl font-semibold text-red-700">Acceso Denegado</h2></div>;
    }

    return (
        <React.Fragment>
            {isFormVisibleForAdd && !currentPackage ? (
                <PackageForm onSubmit={handleAddPackage} onCancel={() => setIsFormVisibleForAdd(false)} initialData={null} isLoading={isLoading} showAppModal={showModal} />
            ) : (
                <>
                    {permissions.REGISTRO_GENERAL?.upload && (
                        <div className="mb-6 p-4 bg-white rounded-lg shadow">
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">Cargar Paquetes desde CSV</h3>
                            <input type="file" id="csvFileInput" accept=".csv" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(e.target.files[0]);}}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100 disabled:opacity-50"
                                disabled={isUploading} />
                            {isUploading && <p className="text-sm text-sky-600 mt-2 animate-pulse">Procesando archivo...</p>}
                        </div>
                    )}

                    <div className="mb-6 p-4 bg-white rounded-lg shadow">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3">Filtro de Datos por Fecha</h3>
                        <div className="flex items-center justify-start gap-4">
                            <div className="flex items-center gap-2">
                                <label htmlFor="year-filter" className="font-medium text-gray-600">Año:</label>
                                <select
                                    id="year-filter"
                                    value={selectedYear}
                                    onChange={(e) => {
                                        setSelectedYear(e.target.value);
                                        setSelectedMonth('');
                                    }}
                                    className="p-2 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500 bg-white"
                                >
                                    <option value="">Seleccione Año</option>
                                    {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <label htmlFor="month-filter" className="font-medium text-gray-600">Mes:</label>
                                <select
                                    id="month-filter"
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    disabled={!selectedYear}
                                    className="p-2 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500 bg-white disabled:bg-gray-100"
                                >
                                    <option value="">Seleccione Mes</option>
                                    {selectedYear && availableMonths[selectedYear]?.map(month => (
                                        <option key={month} value={month}>
                                            {new Date(0, month - 1).toLocaleString('es-GT', { month: 'long' })}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                        {permissions.REGISTRO_GENERAL?.create && (
                           <button onClick={openFormForNew} className="btn-primary w-full md:w-auto">+ Registrar Paquete Manual</button>
                        )}
                        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                            <input type="text" placeholder="Buscar por texto o fecha (dd/mm/aaaa)..." value={searchTermGeneral} onChange={(e) => setSearchTermGeneral(e.target.value)} className="p-3 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500 w-full sm:flex-1"/>
                            <select value={selectedStatusGeneral} onChange={(e) => setSelectedStatusGeneral(e.target.value)} className="p-3 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500 bg-white w-full sm:flex-1">
                                <option value="">Todos los Estados</option>
                                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>

                    {isLoading && <div className="text-center py-10"><p className="animate-pulse">Cargando paquetes del mes seleccionado...</p></div>}

                    {!isLoading && !packages.length && selectedYear && selectedMonth && (
                        <div className="text-center py-10 bg-gray-50 rounded-lg">
                            <p className="text-gray-500">No se encontraron paquetes para {new Date(selectedYear, selectedMonth - 1).toLocaleString('es-GT', { month: 'long' })} de {selectedYear}.</p>
                        </div>
                    )}

                    {!isLoading && !selectedYear && !selectedMonth && (
                         <div className="text-center py-10 bg-sky-50 border border-sky-200 rounded-lg">
                            <p className="text-sky-700 font-semibold">Por favor, selecciona un año y un mes para ver los paquetes.</p>
                        </div>
                    )}
                    
                    {packages.length > 0 && (
                        <>
                            <div className="my-4 flex justify-end">
                               <button onClick={handleExportGeneralCSV} className="btn-success text-sm">Descargar Vista Actual (CSV)</button>
                            </div>
                            <PackageList 
                                packages={filteredAndSortedPackagesGeneral} 
                                onDelete={permissions.REGISTRO_GENERAL?.delete ? handleDeletePackage : null} 
                                onEdit={openFormForEdit} 
                                showEditButton={true}
                                sortCriteria={sortCriteria} 
                                setSortCriteria={setSortCriteria}
                                SortableHeader={SortableHeader} 
                            />
                        </>
                    )}
                </>
            )}

            <Modal isOpen={isEditModalOpen} onClose={() => { setIsEditModalOpen(false); setCurrentPackage(null); }}>
                {currentPackage && <PackageForm onSubmit={(data) => handleUpdatePackage(currentPackage.id, data)} onCancel={() => { setIsEditModalOpen(false); setCurrentPackage(null); }} initialData={currentPackage} isLoading={isLoading} showAppModal={showModal} />}
            </Modal>
        </React.Fragment>
    );
}

export default RegistroGeneral;